import { ReactElement } from 'react';
import { TenantPerformableFormProps } from '~/tenants/common/form';
import { BreOrderContext } from '~/tenants/bre/model/BreOrderContext';
import FormGroup from '~/components/form/FormGroup';
import { FormHorizontal } from '~/components/form/layout';
import { InputCardOption } from '~/components/input/InputCards';
import ZodFieldCards from '~/components/zod/ZodFieldCards';
import ZodFieldSelect from '~/components/zod/ZodFieldSelect';
import { CONTENT_WRITING_RATE, isBusinessDay, plusBusinessDays } from '~/tenants/bre/performable/common';
import {
  BrochurePaper,
  BrochureSide,
  countSchema,
  paperSchema,
  sideSchema,
} from '~/tenants/bre/performable/print/brochures/BreBrochuresConfig';
import { BreBrochuresRate } from '~/tenants/bre/performable/print/brochures/BreBrochuresData';
import { useZodFormFieldSingleValue } from '~/components/zod/ZodForm';
import { InputSelectOption } from '~/components/input/InputSelect';
import ZodFieldCheckbox from '~/components/zod/ZodFieldCheckbox';
import { TenantPerformableAddOns } from '~/tenants/common/performable';
import { TenantPackageAddOnCards } from '~/tenants/common/package';
import BreBrochuresJob from '~/tenants/bre/performable/print/brochures/BreBrochuresJob';
import { useTz } from '~/components/hooks/useTz';
import ZodFieldDate from '~/components/zod/ZodFieldDate';
import { layoutSchema } from '~/tenants/bre/performable/print/design/BreDesignConfig';
import { BreDesignRate } from '~/tenants/bre/performable/print/design/BreDesignData';
import { capitalize } from '~/lib/format';
import { TenantPackageAddOnCheckboxName } from '~/tenants/common/package';

const SIDE_OPTS = sideSchema.options.map<InputCardOption>((value) => ({
  title: `${value} Pages`,
  description: `brochures`,
  value,
}));

const COUNT_OPTS = countSchema.options.map<InputCardOption>((value) => ({
  title: `${value} pieces`,
  description: 'print',
  value,
}));

export default function BreBrochuresForm({ persona }: TenantPerformableFormProps<BreOrderContext>): ReactElement {
  const tz = useTz();
  const sides = useZodFormFieldSingleValue<BrochureSide>('sides') ?? '8';

  const paperOption = paperSchema.options
    .filter((value) => BreBrochuresRate[sides]?.[value])
    .map<InputSelectOption>((value) => ({
      label: BreBrochuresJob.paperType({ paper: value }),
      value,
    }));

  const designOptions = layoutSchema.options.map<InputCardOption>((value) => {
    const rate = BreDesignRate[value][`brochure_${sides}`];

    return {
      title: `${capitalize(value)} Layout`,
      cost: rate,
      disabled: !rate,
      value,
    };
  });

  return (
    <FormGroup>
      <FormHorizontal name="sides" label="Sides" required>
        <ZodFieldCards name="sides" options={SIDE_OPTS} required />
      </FormHorizontal>
      <FormHorizontal name="count" label="Count" required>
        <ZodFieldCards name="count" options={COUNT_OPTS} cols={2} required />
      </FormHorizontal>
      <FormHorizontal name="paper" label="Paper" required>
        <ZodFieldSelect name="paper" options={paperOption} />
      </FormHorizontal>
      <FormHorizontal name="no_free_uv" label="UV Cover">
        <label className="flex gap-2 cursor-pointer" htmlFor="no_free_uv">
          <ZodFieldCheckbox id="no_free_uv" name="no_free_uv" />
          Opt-out of free UV cover
        </label>
      </FormHorizontal>
      <FormHorizontal name={'need_by'} label="Need By" required>
        <ZodFieldDate
          name="need_by"
          filter={(date) => isBusinessDay(date) && date >= plusBusinessDays(tz().startOf('day'), 2)}
        />
      </FormHorizontal>

      <TenantPerformableAddOns persona={persona}>
        <TenantPackageAddOnCheckboxName
          name="content_writing"
          title="Content writing"
          description="Add content writing for all of your marketing pieces"
          image="tenant/bre/print/content_writing.jpg"
          persona={persona}
          cost={CONTENT_WRITING_RATE}
        />
        <TenantPackageAddOnCards
          name="design"
          title="Graphic Design only"
          description="Include layout design to your flyers"
          image="tenant/bre/print/design.jpg"
          persona={persona}
          options={designOptions}
          cols={2}
          required
        />
      </TenantPerformableAddOns>
    </FormGroup>
  );
}
